import React from 'react'
// libs
import { useDropzone } from 'react-dropzone'
// types
import { Field } from 'redux-form'
import { AttachmentType } from '../types'
import { WrappedFieldProps } from 'redux-form/lib/Field'
import { AssetType, IField } from 'types/graphql'
// components
import AttachmentDropzone from './components/AttachmentDropzone'
// hooks
import { useFileUpload } from 'hooks/useFileUpload'
import { useNetworkStatus } from 'hooks/useNetworkStatus'
import { useAttachmentField } from '../useAttachmentField'
// utils
import { ALLOWED_MIME_TYPES } from 'utils/index'
import { registerPendingUpload, clearPendingUpload } from 'utils/pendingFileUploads'

interface Props extends WrappedFieldProps {
  field: IField
  required: boolean
  isDisabled?: boolean
}

const DocumentOrLink: React.FC<Props> = ({ field, required, input, meta, isDisabled }) => {
  const form = meta.form
  const onLine = useNetworkStatus()

  const { upload } = useFileUpload()

  const uploadRef = React.useRef(upload)
  const fileRef = React.useRef<File | null>(null)

  React.useEffect(() => {
    uploadRef.current = upload
  }, [upload])

  const clearExtra = () => {
    clearPendingUpload(form, input.name)
    fileRef.current = null
  }

  const { attachment, setValue, clearField, showAddLinkModal } = useAttachmentField({
    input,
    form,
    onAfterClear: clearExtra,
    onAfterLinkSet: clearExtra,
  })

  const processFile = (file?: File) => {
    if (!file) {
      return
    }

    fileRef.current = file

    setValue({ name: file.name, url: '', type: AttachmentType.Image, _pending: true })

    registerPendingUpload(form, input.name, async () => {
      const currentFile = fileRef.current

      if (!currentFile) return

      const isMsg = currentFile.type === 'application/vnd.ms-outlook' || currentFile.name.toLowerCase().endsWith('.msg')
      const contentType = isMsg ? 'application/vnd.ms-outlook' : currentFile.type
      const url = await uploadRef.current(currentFile, {
        file: {
          assetType: AssetType.ActivityFile,
          contentType,
          entityPrefix: `entity/${field.id}/assets`,
          originalFileName: currentFile.name
        }
      })
      setValue({ name: currentFile.name, url, type: AttachmentType.Image })
      fileRef.current = null
    })
  }

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: ([file]) => processFile(file),
    noClick: true,
    multiple: false,
    disabled: isDisabled,
    // prevents the outer withDropzone from also catching the drop (nested dropzones)
    noDragEventsBubbling: true,
    accept: ALLOWED_MIME_TYPES.reduce((acc, mime) => ({ ...acc, [mime]: [] }), {})
  })

  return (
    <AttachmentDropzone
      field={field}
      required={required}
      isDisabled={isDisabled}
      onLine={onLine}
      attachment={attachment}
      isDragActive={isDragActive}
      onBrowse={open}
      onClear={clearField}
      onAddLink={showAddLinkModal}
      getRootProps={getRootProps}
      getInputProps={getInputProps}
    />
  )
}

const FieldComponent = (props) => <Field {...props} component={DocumentOrLink} />

export default FieldComponent
